import React from 'react';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import VehicleServiceHero from '../../../../components/ui/VehicleServiceHero';
import { CheckCircle, Wrench, Shield, Clock, DollarSign, Award, Car, Settings, AlertTriangle, Star } from 'lucide-react';
import { Link } from 'react-router-dom';

const MiniCooperService = () => {
  const miniFeatures = [
    'MINI Expert Technicians',
    'BMW ISTA Diagnostics',
    'Cooper S Performance Experts',
    'JCW Specialists',
    'Genuine MINI Parts',
    'British Heritage Service'
  ];

  const fadeInUp = {
    initial: { opacity: 0, y: 20 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.6 }
  };

  const commonIssues = [
    { 
      model: 'Cooper S (R56/F56)', 
      years: '2007-Present',
      issues: ['Timing chain tensioner', 'Turbo oil lines', 'Carbon buildup', 'Thermostat housing'],
      solution: 'Preventive maintenance and early intervention'
    },
    {
      model: 'Countryman/Clubman',
      years: '2011-Present',
      issues: ['High pressure fuel pump', 'Water pump', 'CVT transmission', 'ALL4 system'],
      solution: 'Regular service and OEM parts replacement'
    },
    {
      model: 'First Generation (R50/R53)',
      years: '2002-2006',
      issues: ['Power steering pump', 'Supercharger service', 'CVT failure', 'Mushrooming strut towers'],
      solution: 'Specialized first-gen MINI expertise'
    },
    {
      model: 'JCW Models',
      years: '2008-Present',
      issues: ['Turbo failures', 'Brake wear', 'Suspension components', 'Engine cooling'],
      solution: 'Performance-focused maintenance program'
    }
  ];

  const services = [
    {
      title: 'Maintenance Services',
      items: [
        'Condition Based Service',
        'Oil Service',
        'Microfilter Service',
        'Brake Fluid Service',
        'Spark Plug Service'
      ]
    },
    {
      title: 'Engine Services',
      items: [
        'Timing Chain Repair',
        'Turbo Replacement',
        'Carbon Cleaning',
        'Thermostat Housing',
        'Water Pump Service'
      ]
    },
    {
      title: 'Performance',
      items: [
        'JCW Upgrades',
        'ECU Remapping',
        'Exhaust Systems',
        'Suspension Tuning',
        'Brake Upgrades'
      ]
    },
    {
      title: 'Specialized Repair',
      items: [
        'BMW ISTA Coding',
        'Convertible Top Repair',
        'ALL4 System Service',
        'Navigation Updates',
        'Key Programming'
      ]
    }
  ];

  return (
    <>
      <Helmet>
        <title>MINI Cooper Repair in Dallas: British Engineering Experts | Paradise-Performance</title>
        <meta name="description" content="Expert MINI Cooper repair & service in Dallas. European auto specialists, dealer-level expertise, N14 timing chain experts. Save 25-40% vs dealership. Call (817) 382-2316." />
        <link rel="canonical" href="https://paradise-performance.com/services/european-car-repair/mini-cooper" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://paradise-performance.com/services/european-car-repair/mini-cooper" />
        <meta property="og:title" content="MINI Cooper Repair Dallas | British Car Specialists" />
        <meta property="og:description" content="Dallas MINI specialists. Timing chain experts, Cooper S, JCW service. 25-40% less than dealership. Call (817) 382-2316." />
        <meta property="og:image" content="https://paradise-performance.com/assets/images/mini-repair.webp" />
        
        {/* Twitter */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="MINI Cooper Repair Dallas | British Car Specialists" />
        <meta name="twitter:description" content="Dallas MINI specialists. Timing chain experts, Cooper S, JCW service. 25-40% less than dealership." />
        <meta name="twitter:image" content="https://paradise-performance.com/assets/images/mini-repair.webp" />
        
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "AutoRepair",
            "name": "MINI Cooper Service - Paradise-Performance",
            "description": "Expert MINI Cooper repair and performance services",
            "telephone": "(817) 382-2316",
            "address": {
              "@type": "PostalAddress",
              "streetAddress": "305 Co Rd 3470",
              "addressLocality": "Paradise",
              "addressRegion": "TX",
              "postalCode": "76073"
            },
            "areaServed": "Dallas-Fort Worth",
            "brand": "MINI"
          })}
        </script>

        {/* FAQ Schema for Featured Snippets */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": [
              {
                "@type": "Question",
                "name": "How much does MINI Cooper maintenance cost?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MINI Cooper maintenance costs average $846 to $1,200 per year depending on the model. An oil change runs $90-$130 at an independent shop (vs $150-$220 at dealerships). Timing chain replacement costs $1,500-$2,500. At Paradise-Performance near Dallas, we're typically 25-40% less than MINI dealership prices."
                }
              },
              {
                "@type": "Question",
                "name": "Are MINI Coopers expensive to maintain?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MINI Coopers cost more to maintain than mainstream compact cars, averaging $846-$1,200 annually. Major repairs like timing chain service ($1,500-$2,500) and turbo replacement ($1,800-$3,000) can be costly. However, using an independent European specialist instead of the dealership saves 25-40%."
                }
              },
              {
                "@type": "Question",
                "name": "What are common MINI Cooper problems?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "Common MINI Cooper issues include: timing chain tensioner failure (N14/N18 engines), carbon buildup on intake valves, thermostat housing cracks, water pump failures, high-pressure fuel pump issues, and turbo oil line leaks. First-gen R50/R53 models have power steering pump and supercharger issues. Regular maintenance prevents most problems."
                }
              },
              {
                "@type": "Question",
                "name": "How often should I service my MINI Cooper?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "MINI recommends service every 10,000-15,000 miles via the Condition Based Service system. We recommend: oil change every 7,500 miles (especially for turbo models), brake fluid every 2 years, spark plugs every 60,000 miles, and carbon cleaning every 40,000-60,000 miles on direct injection engines."
                }
              },
              {
                "@type": "Question",
                "name": "Is the MINI Cooper S reliable?",
                "acceptedAnswer": {
                  "@type": "Answer",
                  "text": "The MINI Cooper S is reasonably reliable when properly maintained. The N14 engine (2007-2010) has known timing chain issues—budget for preventive replacement around 80,000 miles. The newer N18/B48 engines are more reliable. Common issues include carbon buildup, thermostat housing leaks, and water pump failures. Regular service keeps Cooper S models running well past 150,000 miles."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <VehicleServiceHero
        brand="MINI Cooper"
        brandColor="#00AA4E"
        tagline="British Engineering Experts"
        description="When your MINI needs specialized service, choose Paradise-Performance. Our European auto specialists understand the unique requirements of MINI's distinctive engineering, providing dealer-level expertise without the premium pricing."
        features={miniFeatures}
        heroImage="/assets/assets/images/mini-cooper.webp"
        heroImageAlt="MINI Cooper Performance Vehicle"
        heroVariant="default"
      />

      {/* Main Content */}
      <div className="bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4">
          {/* Intro Section */}
          <motion.div {...fadeInUp} className="mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-6 text-center">Specialized MINI Cooper Service by Dallas's European Auto Experts</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-6">
              Though small in stature, MINI Coopers pack sophisticated engineering that requires specialized knowledge to properly maintain and repair. 
              As dedicated European auto repair specialists serving Dallas, we understand the unique challenges these British-engineered vehicles present.
            </p>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-6">
              MINI vehicles feature distinctive technologies that set them apart from other European brands. Despite being owned by BMW, these cars maintain 
              their British heritage with unique engineering approaches, specialized components, and model-specific maintenance requirements that many general 
              repair shops simply don't understand.
            </p>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center">
              With over 15 years of experience providing European auto repair to Dallas MINI owners, we've invested in the specialized diagnostic equipment 
              and training needed to properly care for these distinctive vehicles. This commitment to specialized MINI service is why discerning Dallas owners 
              trust our expertise.
            </p>
          </motion.div>

          {/* Image Gallery Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="grid md:grid-cols-2 gap-8">
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/mini-cooper.webp"
                  alt="MINI Cooper service specialist"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">British Engineering Experts</h3>
                    <p className="text-white/90">Specialized service for all MINI models</p>
                  </div>
                </div>
              </div>
              <div className="relative h-96 overflow-hidden rounded-lg shadow-2xl">
                <img 
                  src="/assets/assets/images/new/euro-repair-img/bmw-turbo-leak.webp"
                  alt="MINI Cooper turbo service"
                  className="w-full h-full object-cover hover:scale-105 transition-transform duration-500"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent flex items-end">
                  <div className="p-6">
                    <h3 className="text-2xl font-bold text-white mb-2">Cooper S Turbo Specialists</h3>
                    <p className="text-white/90">Performance maintenance for turbocharged models</p>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Complete Service Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Complete MINI Cooper Service & Repair for Dallas Drivers</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-8">
              From factory-scheduled maintenance to complex diagnostics and repairs, our European auto repair specialists provide comprehensive care for your MINI:
            </p>

            {/* Service Cards */}
            <div className="grid md:grid-cols-3 gap-8">
              {/* Maintenance Card */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-bold text-[#00AA4E] mb-4">MINI Cooper Maintenance</h3>
                <p className="text-gray-700 mb-4">
                  Prevent costly repairs with our specialized maintenance services designed specifically for MINI's unique requirements:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Oil service using only MINI-approved synthetic oils (5,000-mile intervals recommended for Dallas)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Inspection services following guidelines with additional checks for common MINI failure points</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Brake fluid service (critical in Dallas humidity)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Cooling system maintenance (essential for Dallas summers)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Filter replacements (air, cabin, fuel) at appropriate intervals</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Climate control service to maintain optimal cooling</span>
                  </li>
                </ul>
                <div className="bg-green-50 rounded-lg p-3">
                  <p className="text-lg font-bold text-green-700">Maintenance packages starting at $159</p>
                  <p className="text-sm text-green-600">(vs. MINI dealership $239+)</p>
                </div>
              </div>

              {/* Diagnostics Card */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-bold text-[#00AA4E] mb-4">MINI Cooper Diagnostics</h3>
                <p className="text-gray-700 mb-4">
                  Our specialized MINI diagnostic equipment identifies issues with precision that generic shops simply can't match:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Check engine light diagnosis with MINI-specific scan tools</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Electrical system troubleshooting for MINI's complex networks</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Drivetrain fault analysis including transmission control</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Performance testing to identify drivability issues</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Cooling system evaluation to prevent overheating</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Pre-purchase inspections for used MINIs</span>
                  </li>
                </ul>
                <div className="bg-blue-50 rounded-lg p-3">
                  <p className="text-lg font-bold text-blue-700">Diagnostic fee: $95</p>
                  <p className="text-sm text-blue-600">(waived with repair)</p>
                </div>
              </div>

              {/* Repairs Card */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-2xl font-bold text-[#00AA4E] mb-4">MINI Cooper Repairs</h3>
                <p className="text-gray-700 mb-4">
                  Our European auto repair expertise ensures your MINI gets the precise service it requires:
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Engine repairs including timing chain replacement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Transmission service for both manual and automatic</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Cooling system repairs (thermostat, water pump, radiator)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Suspension and steering including electric power steering</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Supercharger and turbocharger maintenance for S models</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <span className="text-gray-700">Electrical repairs for MINI's sophisticated systems</span>
                  </li>
                </ul>
                <div className="bg-yellow-50 rounded-lg p-3">
                  <p className="text-lg font-bold text-yellow-700">All repairs include</p>
                  <p className="text-sm text-yellow-600">12-month/12,000-mile warranty</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Models We Service */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MINI Cooper Models We Service for Dallas Owners</h2>
            <div className="grid md:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">MINI Hardtop Models</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• MINI Cooper & Cooper S (R50/R53)</li>
                  <li>• Second Generation (R56)</li>
                  <li>• Third Generation (F55/F56)</li>
                  <li>• Current Generation (2-door & 4-door)</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">MINI Convertible & Specialty</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• All Convertible generations</li>
                  <li>• MINI Coupe & Roadster</li>
                  <li>• MINI Clubman (R55, F54)</li>
                  <li>• MINI John Cooper Works models</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">MINI Countryman & SUVs</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• First Generation (R60)</li>
                  <li>• Current Generation (F60)</li>
                  <li>• All4 AWD systems</li>
                  <li>• Countryman S & JCW variants</li>
                </ul>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">Electrified MINIs</h3>
                <ul className="space-y-2 text-gray-700">
                  <li>• MINI Electric</li>
                  <li>• MINI Cooper SE</li>
                  <li>• Hybrid Countryman models</li>
                  <li>• Upcoming electric models</li>
                </ul>
              </div>
            </div>
          </motion.div>

          {/* Timing Chain Special Section */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-900 to-black rounded-xl p-8 text-white mb-16">
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <h3 className="text-3xl font-bold mb-4">N14 Engine Timing Chain Service</h3>
                <p className="mb-4">
                  The N14 engine timing chain is a known weak point in 2007-2010 Cooper S models. 
                  Our preventive timing chain service can save you from catastrophic engine failure.
                </p>
                <ul className="space-y-2 mb-6">
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#00AA4E] mr-2" />
                    <span>Complete timing chain kit replacement</span>
                  </li>
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#00AA4E] mr-2" />
                    <span>Updated tensioner and guides</span>
                  </li>
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#00AA4E] mr-2" />
                    <span>Oil pump chain included</span>
                  </li>
                  <li className="flex items-center">
                    <Shield className="h-5 w-5 text-[#00AA4E] mr-2" />
                    <span>2-year warranty on parts and labor</span>
                  </li>
                </ul>
                <Link
                  to="/contact"
                  className="inline-block bg-[#00AA4E] text-white px-6 py-3 rounded font-semibold hover:bg-green-700 transition"
                >
                  Schedule Timing Chain Service
                </Link>
              </div>
              <div className="bg-gray-800 rounded-lg p-6">
                <h4 className="text-xl font-semibold mb-4 text-[#00AA4E]">Affected Models</h4>
                <ul className="space-y-2 text-gray-300">
                  <li>• 2007-2010 Cooper S (R56)</li>
                  <li>• 2008-2012 Clubman S (R55)</li>
                  <li>• 2011-2014 Countryman S (R60)</li>
                  <li>• 2013-2015 Paceman S (R61)</li>
                  <li>• All N14 engine vehicles</li>
                </ul>
                <p className="mt-4 text-sm text-gray-400">
                  Don't wait for the death rattle - preventive service recommended at 70,000 miles
                </p>
              </div>
            </div>
          </motion.div>

          {/* Common Issues Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Common MINI Cooper Issues Dallas Drivers Experience</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-10">
              MINI Coopers have specific mechanical traits and potential issues that differ significantly from other European brands. 
              Our European auto specialists have extensive experience diagnosing and repairing these MINI-specific concerns:
            </p>

            {/* Timing Chain Tensioner Issue */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-8 h-8 text-red-500 mr-4 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Timing Chain Tensioner Failures</h3>
                  <p className="text-gray-700 mb-4">
                    One of the most serious issues affecting MINI Coopers, particularly 2007-2010 models with the N14 engine and 2011-2013 models with the N16 engine. 
                    This can lead to catastrophic engine failure if not addressed proactively.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Warning signs include:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Rattling noise from the engine during cold starts</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Check engine light with timing-related codes</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Sudden loss of power or engine performance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-red-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Oil pressure fluctuations</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Our Approach:</h4>
                  <p className="text-gray-700 mb-3">
                    Unlike other European brands, MINI's timing chain system has a unique design that's particularly vulnerable to failure. 
                    In our experience serving Dallas MINI owners, we recommend preventative timing chain replacement around 60,000-80,000 miles 
                    for affected models—well before the 100,000-mile mark when catastrophic failures become common.
                  </p>
                  <p className="text-gray-700">
                    The Dallas climate accelerates this issue, as heat cycling causes more rapid degradation of the plastic chain guides. 
                    We've saved numerous customers from complete engine failures by identifying early warning signs during routine oil services.
                  </p>
                </div>
              </div>
            </div>

            {/* Thermostat Housing Issue */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-8 h-8 text-orange-500 mr-4 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Thermostat Housing & Coolant Leaks</h3>
                  <p className="text-gray-700 mb-4">
                    MINI's cooling system has several design weaknesses that manifest particularly badly in Dallas's extreme temperatures. 
                    The thermostat housing is made of plastic that becomes brittle over time, leading to cracks and leaks.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Common symptoms include:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Coolant leaks at the front of the engine</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Sweet smell from the engine bay</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Temperature gauge fluctuations</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Low coolant warnings</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-orange-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Overheating in Dallas summer traffic</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Our Solution:</h4>
                  <p className="text-gray-700 mb-3">
                    The plastic thermostat housing design on MINIs differs from other European brands and requires specialized knowledge to service properly. 
                    When we repair these issues, we use upgraded aluminum components rather than replacing with the same failure-prone plastic parts. 
                    This adaptation is especially important for Dallas MINI owners, as our summer heat accelerates failure of the original components.
                  </p>
                  <p className="text-gray-700">
                    Additionally, we inspect the water pump and radiator during cooling system repairs, as these components often fail within a similar 
                    timeframe and share labor for replacement. This comprehensive approach prevents repeated cooling system failures that plague many Dallas MINI owners.
                  </p>
                </div>
              </div>
            </div>

            {/* Electric Power Steering Issue */}
            <div className="bg-white rounded-xl shadow-lg p-8 mb-8">
              <div className="flex items-start mb-4">
                <AlertTriangle className="w-8 h-8 text-yellow-500 mr-4 flex-shrink-0" />
                <div className="flex-1">
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Electric Power Steering Failures</h3>
                  <p className="text-gray-700 mb-4">
                    MINI Coopers use a unique electric power steering system that differs substantially from other European brands. 
                    The system is prone to failures, particularly in models from 2002-2013.
                  </p>
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Symptoms of power steering issues:</h4>
                  <ul className="space-y-2">
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Sudden loss of power steering assistance</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Steering wheel feels heavy, especially at low speeds</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Power steering warning light on dashboard</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Whining noise when turning the wheel</span>
                    </li>
                    <li className="flex items-start">
                      <CheckCircle className="w-5 h-5 text-yellow-500 mr-2 mt-1 flex-shrink-0" />
                      <span>Intermittent steering assistance</span>
                    </li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-gray-800 mb-3">Expert Diagnosis:</h4>
                  <p className="text-gray-700 mb-3">
                    The electric power steering system in MINIs uses a column-mounted motor with unique electronic controls. 
                    Unlike hydraulic systems used in many other European vehicles, this system doesn't use fluid, making diagnosis 
                    more challenging for shops without MINI-specific experience.
                  </p>
                  <p className="text-gray-700">
                    For Dallas owners, our approach includes testing the entire system—both mechanical and electrical components—to 
                    identify whether the issue is with the motor, control module, or wiring. Often, we can repair the existing unit 
                    rather than requiring complete replacement, saving our customers significantly compared to the dealership approach.
                  </p>
                </div>
              </div>
            </div>

            {/* Additional Issues Grid */}
            <div className="grid md:grid-cols-3 gap-6">
              {/* Transmission Issues */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#00AA4E] mb-3">Transmission Issues</h4>
                <p className="text-gray-700 mb-3">
                  MINI's manual transmissions and Aisin automatic transmissions have several known issues we regularly address:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Clutch failures occurring earlier than expected</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Hard shifting or hesitation (automatic)</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Failed synchronizers in manual transmissions</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  We specialize in clutch repair & servicing—call if you're experiencing any issues with your manual MINI.
                </p>
              </div>

              {/* Fuel Pump Issues */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#00AA4E] mb-3">High-Pressure Fuel Pump Failures</h4>
                <p className="text-gray-700 mb-3">
                  Turbocharged MINI models, particularly 2007-2012 Cooper S variants, experience frequent fuel pump failures:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Long cranking times before starting</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Loss of power during acceleration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>"Reduced power" warnings</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Direct injection systems require up to 2,000 PSI fuel pressure—we have the specialized equipment to diagnose accurately.
                </p>
              </div>

              {/* Carbon Buildup */}
              <div className="bg-white rounded-lg shadow-md p-6">
                <h4 className="text-xl font-bold text-[#00AA4E] mb-3">Carbon Buildup on Intake Valves</h4>
                <p className="text-gray-700 mb-3">
                  All direct-injection MINI engines (most models after 2007) develop carbon deposits leading to:
                </p>
                <ul className="space-y-2 text-sm">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Rough idle, especially when cold</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Hesitation during acceleration</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                    <span>Reduced fuel economy</span>
                  </li>
                </ul>
                <p className="text-sm text-gray-600 mt-3">
                  Our walnut blasting service removes deposits without engine disassembly. Recommended every 40,000-60,000 miles for Dallas drivers.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Dallas-Specific Maintenance */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Dallas-Specific MINI Cooper Maintenance Recommendations</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-8">
              The Dallas climate and driving conditions create unique maintenance needs for MINI owners. Our European auto specialists 
              recommend these adjustments to standard maintenance intervals:
            </p>
            <div className="bg-yellow-50 border-l-4 border-yellow-600 rounded-r-lg p-6">
              <ul className="space-y-4">
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Oil changes every 5,000 miles</strong> — MINI recommends 10,000-mile intervals, 
                    but Dallas heat accelerates oil breakdown. Using only MINI-approved synthetic oil is critical.
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Cooling system inspections before summer</strong> — Proactive checks prevent 
                    being stranded with an overheating MINI in Dallas traffic.
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Transmission fluid service every 30,000-40,000 miles</strong> — Despite MINI claiming 
                    "lifetime" fluids, Dallas stop-and-go traffic causes accelerated fluid degradation.
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Brake fluid replacement every 2 years</strong> — MINI's brake systems are particularly 
                    sensitive to fluid contamination, and Dallas humidity accelerates moisture absorption.
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Battery testing twice yearly</strong> — MINI's electrical systems put significant 
                    demands on the battery, and Texas heat typically cuts battery life in half compared to cooler climates.
                  </div>
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-6 h-6 text-green-500 mr-3 mt-1 flex-shrink-0" />
                  <div>
                    <strong className="text-gray-900">Air conditioning service annually</strong> — Critical for comfort during Dallas 
                    summers and prevents compressor failures that are common in older MINIs.
                  </div>
                </li>
              </ul>
            </div>
          </motion.div>

          {/* JCW Performance Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <div className="bg-gradient-to-r from-[#00AA4E] to-green-700 rounded-xl overflow-hidden">
              <div className="p-8">
                <h3 className="text-3xl font-bold text-white mb-6 text-center">John Cooper Works Performance</h3>
                <p className="text-white text-center mb-8 max-w-3xl mx-auto">
                  Take your MINI to the next level with our JCW performance services. From genuine JCW upgrades 
                  to custom tuning solutions, we help you maximize your MINI's potential.
                </p>
                <div className="grid md:grid-cols-3 gap-6">
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">JCW Tuning</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• ECU remapping</li>
                      <li>• Exhaust systems</li>
                      <li>• Cold air intakes</li>
                      <li>• Intercooler upgrades</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">Suspension & Handling</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• Coilover systems</li>
                      <li>• Sway bars</li>
                      <li>• Strut braces</li>
                      <li>• Performance alignment</li>
                    </ul>
                  </div>
                  <div className="bg-white/10 backdrop-blur rounded-lg p-6">
                    <h4 className="text-white font-semibold mb-3">Brake Systems</h4>
                    <ul className="text-white/90 space-y-2 text-sm">
                      <li>• JCW brake kits</li>
                      <li>• Performance pads</li>
                      <li>• Stainless lines</li>
                      <li>• Track compounds</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Ownership Experience Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Ownership Experience: MINI Cooper in Dallas</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-8">
              Owning a MINI Cooper in the Dallas area presents unique joys and challenges that differ from other European vehicles. 
              Our years of providing European auto repair services to Dallas MINI owners have given us valuable insights:
            </p>
            
            <div className="grid md:grid-cols-2 gap-8">
              {/* Dallas Climate Considerations */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">Dallas Climate Considerations</h3>
                <p className="text-gray-700 mb-4">
                  MINI Coopers have specific vulnerabilities to the Dallas temperature extremes:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Cooling system stress</strong> — MINI's compact engine bay retains more heat than larger European vehicles, 
                      making cooling system maintenance critical in Dallas.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Battery degradation</strong> — The tucked-away battery location in many MINI models exposes it to more heat, 
                      accelerating failure in Texas summers.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Sunroof drain clogs</strong> — Dallas's seasonal pollen and debris can block MINI's notoriously problematic 
                      sunroof drains, leading to interior water damage.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>A/C performance</strong> — While capable, MINI's air conditioning systems are taxed by Texas heat and benefit 
                      from regular maintenance to maintain optimal cooling.
                    </div>
                  </li>
                </ul>
              </div>
              
              {/* Dallas Driving Impact */}
              <div className="bg-white rounded-lg shadow-lg p-6">
                <h3 className="text-xl font-bold text-[#00AA4E] mb-4">Dallas Driving Impact</h3>
                <p className="text-gray-700 mb-4">
                  The Dallas-Fort Worth driving environment affects MINIs differently than other European vehicles:
                </p>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Suspension considerations</strong> — MINI's sporty suspension is more sensitive to Dallas road conditions, 
                      requiring more frequent alignment checks and component inspections.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Stop-and-go effects</strong> — Dallas traffic creates more frequent cold starts and short trips that 
                      accelerate carbon buildup in direct-injection MINIs.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Tire wear patterns</strong> — MINI's unique handling characteristics and weight distribution create specific 
                      tire wear patterns that require monitoring, especially with Dallas's hot pavement.
                    </div>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-green-500 mr-2 mt-1 flex-shrink-0" />
                    <div>
                      <strong>Fuel quality effects</strong> — MINI's high-performance engines are more sensitive to fuel quality variations 
                      common at Dallas gas stations.
                    </div>
                  </li>
                </ul>
              </div>
            </div>
            
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mt-8">
              Understanding these Dallas-specific factors allows us to provide more effective European auto repair services tailored to local MINI owners. 
              Our preventative maintenance recommendations and inspection procedures take these regional challenges into account, helping your MINI deliver 
              years of reliable performance despite the demanding Texas environment.
            </p>
          </motion.div>

          {/* Comparison Table */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">Why Dallas MINI Owners Choose Paradise-Performance</h2>
            <p className="text-lg text-gray-700 max-w-4xl mx-auto text-center mb-8">
              MINI Cooper service requires specialized knowledge and equipment that most repair shops simply don't possess. 
              Here's why discerning Dallas MINI owners make the short drive to our facility:
            </p>
            <div className="overflow-x-auto">
              <table className="min-w-full bg-white rounded-lg shadow-lg overflow-hidden">
                <thead className="bg-[#00AA4E] text-white">
                  <tr>
                    <th className="px-6 py-4 text-left">Service Factor</th>
                    <th className="px-6 py-4 text-left">MINI Dealership</th>
                    <th className="px-6 py-4 text-left">General Repair Shop</th>
                    <th className="px-6 py-4 text-left">Paradise-Performance</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  <tr>
                    <td className="px-6 py-4 font-medium">MINI-Specific Diagnostics</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">MINI-Expert Technicians</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4 text-red-600">✗</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Genuine/OEM Parts</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                    <td className="px-6 py-4">Varies</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Service Customized for Dallas</td>
                    <td className="px-6 py-4">Limited</td>
                    <td className="px-6 py-4">No</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Preventative Maintenance Focus</td>
                    <td className="px-6 py-4">Limited</td>
                    <td className="px-6 py-4">Varies</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr className="bg-gray-50">
                    <td className="px-6 py-4 font-medium">Direct Communication with Technician</td>
                    <td className="px-6 py-4">No</td>
                    <td className="px-6 py-4">Sometimes</td>
                    <td className="px-6 py-4 text-green-600">✓</td>
                  </tr>
                  <tr>
                    <td className="px-6 py-4 font-medium">Average Cost Savings</td>
                    <td className="px-6 py-4">Base Price</td>
                    <td className="px-6 py-4">10-20% less</td>
                    <td className="px-6 py-4 text-green-600">25-40% less</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-center text-gray-700 mt-6">
              <strong>Why this matters:</strong> Your MINI deserves specialized care that respects its unique engineering. 
              Unlike dealerships with their high prices and impersonal service or general shops that lack MINI-specific knowledge, 
              we provide expert European auto repair services with personalized attention at fair prices.
            </p>
          </motion.div>

          {/* Success Story */}
          <motion.div {...fadeInUp} className="bg-gradient-to-r from-gray-100 to-gray-200 rounded-xl p-8 mb-16">
            <h3 className="text-2xl font-bold text-gray-900 mb-4 text-center">MINI Cooper Success Story: When Other Shops Failed</h3>
            <div className="max-w-4xl mx-auto">
              <div className="mb-4">
                <p className="font-bold text-[#00AA4E]">The Challenge:</p>
                <p className="text-gray-700">
                  A Dallas customer brought in her 2013 MINI Cooper S that had stumped three other repair shops. The vehicle would randomly stall while driving, 
                  creating a dangerous situation in Dallas traffic. The other shops had replaced the fuel pump, battery, and numerous sensors, 
                  yet the problem persisted.
                </p>
              </div>
              <div className="mb-4">
                <p className="font-bold text-[#00AA4E]">Our European Auto Repair Approach:</p>
                <p className="text-gray-700">
                  Rather than replacing parts based on guesswork, our MINI specialists performed a comprehensive diagnostic process that included:
                </p>
                <ul className="list-disc pl-6 mt-2 space-y-1">
                  <li>Full system scan with MINI-specific diagnostic equipment</li>
                  <li>Monitoring live data during extended test drives</li>
                  <li>Electrical system load testing under various conditions</li>
                  <li>Circuit testing of the critical engine control systems</li>
                </ul>
              </div>
              <div className="mb-4">
                <p className="font-bold text-[#00AA4E]">The Discovery:</p>
                <p className="text-gray-700">
                  Our technicians identified an intermittent fault in the crankshaft position sensor circuit—not the sensor itself (which had already been replaced) 
                  but a wiring harness connector that was allowing moisture intrusion. This connector design is unique to MINI vehicles and is a known failure point 
                  that general repair shops often miss.
                </p>
              </div>
              <div className="mb-4">
                <p className="font-bold text-[#00AA4E]">The Solution:</p>
                <p className="text-gray-700">
                  We repaired the wiring harness and sealed the connector properly, resolving the issue completely. 
                  The MINI has now run flawlessly for over 18 months, and the customer makes the drive from Dallas for all her maintenance needs, 
                  knowing that our expertise saves her both money and frustration in the long run.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Testimonials */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">What Dallas MINI Owners Say About Paradise-Performance</h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-current text-yellow-500" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-4">
                  "After bouncing between the dealership and a general shop that couldn't solve my MINI's mysterious power loss issue, 
                  I found Paradise-Performance through a MINI owners' forum. Their European auto repair expertise identified and fixed a 
                  timing chain problem that could have destroyed my engine. The drive from Dallas was absolutely worth it—I've been a 
                  loyal customer for three years now."
                </blockquote>
                <p className="font-bold text-gray-900">— Jennifer M., Dallas</p>
                <p className="text-sm text-gray-600">Google Reviews</p>
              </div>
              
              <div className="bg-white rounded-lg shadow-lg p-6">
                <div className="flex mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-6 h-6 fill-current text-yellow-500" />
                  ))}
                </div>
                <blockquote className="text-gray-700 mb-4">
                  "My MINI Countryman needed major service, but the dealership quote was outrageous. Paradise-Performance did the same work 
                  for 40% less, used genuine parts, and took the time to explain everything they found. As a bonus, their European auto repair 
                  shop is much easier to schedule with than Dallas dealerships with their 2-week waits. Highly recommend to any MINI owner in DFW."
                </blockquote>
                <p className="font-bold text-gray-900">— David K., Dallas</p>
                <p className="text-sm text-gray-600">Google Reviews</p>
              </div>
            </div>
          </motion.div>

          {/* FAQ Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">MINI Cooper Repair: Frequently Asked Questions</h2>
            <div className="space-y-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  How do MINI Cooper maintenance requirements differ from other European vehicles in Dallas?
                </h3>
                <p className="text-gray-700 mb-3">
                  MINI Coopers have several unique maintenance requirements compared to other European brands that are amplified by Dallas conditions:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700">
                  <li>More frequent cooling system maintenance due to the compact engine bay and heat retention in Dallas temperatures</li>
                  <li>Specialized transmission fluids that differ from those used in parent company BMW's vehicles</li>
                  <li>Unique electrical system architecture that requires specific diagnostic approaches</li>
                  <li>Timing chain system vulnerabilities that require more vigilant monitoring in Dallas driving conditions</li>
                  <li>Suspension components that wear differently due to MINI's distinctive weight distribution and Dallas road conditions</li>
                </ul>
                <p className="text-gray-700 mt-3">
                  Our European auto repair shop tailors maintenance schedules specifically for Dallas MINI owners, accounting for these unique requirements and local conditions.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">Are MINIs reliable in the Dallas climate?</h3>
                <p className="text-gray-700 mb-3">
                  MINIs can be reliable in Dallas when properly maintained, but they do face specific challenges in our climate:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li>The cooling systems are more stressed by Dallas heat than those in larger European vehicles</li>
                  <li>Plastic components deteriorate faster in Texas heat cycles, particularly in cooling systems</li>
                  <li>Electric power steering systems are more prone to failure in high temperatures</li>
                  <li>Battery life is typically 2-3 years shorter in Dallas compared to cooler regions</li>
                </ul>
                <p className="text-gray-700">
                  With proper preventative maintenance tailored to Dallas conditions, we've helped many MINI owners enjoy 150,000+ trouble-free miles. 
                  The key is addressing known weak points proactively rather than waiting for failures to occur, particularly with cooling system components and timing chains.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  Is it worth driving from Dallas to Paradise for MINI Cooper service?
                </h3>
                <p className="text-gray-700 mb-3">
                  Many of our loyal Dallas customers tell us the drive is worthwhile for several compelling reasons:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li>Cost savings — Typically 25-40% less than Dallas MINI dealership prices</li>
                  <li>Expert knowledge — Specialized focus on European vehicles with MINI-specific training</li>
                  <li>Personal attention — Direct communication with the technician working on your car</li>
                  <li>Faster scheduling — No multi-week waits common at urban dealerships</li>
                  <li>Preventative focus — Recommendations tailored to Dallas conditions that prevent costly repairs</li>
                </ul>
                <p className="text-gray-700">
                  The 45-minute drive from downtown Dallas translates to significant savings, both financially and in preventing repeated repair attempts 
                  by shops unfamiliar with MINI's unique engineering. Many customers schedule service during off-peak traffic hours, combining their visit 
                  with other errands to maximize convenience.
                </p>
              </div>
              
              <div className="bg-white rounded-lg shadow-md p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-3">
                  What are the most reliable MINI Cooper models for Dallas drivers?
                </h3>
                <p className="text-gray-700 mb-3">
                  Based on our experience servicing hundreds of MINIs in the Dallas area, we've observed these reliability patterns:
                </p>
                <ul className="list-disc pl-6 space-y-2 text-gray-700 mb-3">
                  <li><strong>Most Reliable:</strong> 2014-present third-generation MINIs (F56 platform) with B-series engines have significantly fewer serious issues</li>
                  <li><strong>Improved Reliability:</strong> 2011-2013 models with the N18 engine addressed many of the earlier timing chain and oil consumption issues</li>
                  <li><strong>Most Problematic:</strong> 2007-2010 models with the N14 engine experience the highest rates of significant failures, particularly in Dallas heat</li>
                  <li><strong>First Generation (2002-2006):</strong> Generally reliable with preventative maintenance, though electrical issues increase with age</li>
                </ul>
                <p className="text-gray-700">
                  For Dallas conditions specifically, we find that naturally-aspirated models tend to handle the heat better than turbocharged S variants, 
                  though all MINIs require specialized European auto repair knowledge for optimal longevity. The current generation offers the best combination 
                  of reliability and modern features for our local climate and driving conditions.
                </p>
              </div>
            </div>
          </motion.div>

          {/* Other European Vehicles Section */}
          <motion.div {...fadeInUp} className="mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center">Other European Vehicles We Service in the Dallas Area</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-shadow">
                <Car className="w-12 h-12 text-blue-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">BMW</h3>
                <p className="text-gray-700 mb-4">Complete BMW service and repair</p>
                <Link to="/services/european-car-repair/bmw" className="text-blue-600 font-semibold hover:text-blue-700">
                  Learn More →
                </Link>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-shadow">
                <Car className="w-12 h-12 text-gray-800 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Audi</h3>
                <p className="text-gray-700 mb-4">Quattro specialists and expert Audi service</p>
                <Link to="/services/european-car-repair/audi" className="text-gray-800 font-semibold hover:text-gray-900">
                  Learn More →
                </Link>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-shadow">
                <Car className="w-12 h-12 text-red-600 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Porsche</h3>
                <p className="text-gray-700 mb-4">Performance-focused Porsche maintenance</p>
                <Link to="/services/european-car-repair/porsche" className="text-red-600 font-semibold hover:text-red-700">
                  Learn More →
                </Link>
              </div>
              <div className="bg-white rounded-lg shadow-md p-6 text-center hover:shadow-xl transition-shadow">
                <Car className="w-12 h-12 text-green-700 mx-auto mb-3" />
                <h3 className="text-xl font-bold text-gray-900 mb-2">Land Rover</h3>
                <p className="text-gray-700 mb-4">Range Rover and Land Rover experts</p>
                <Link to="/services/european-car-repair/land-rover" className="text-green-700 font-semibold hover:text-green-800">
                  Learn More →
                </Link>
              </div>
            </div>
          </motion.div>

          {/* Final CTA Section */}
          <motion.div {...fadeInUp} className="text-center bg-gradient-to-r from-[#00AA4E] to-green-700 rounded-xl p-12 text-white">
            <Car className="w-16 h-16 mx-auto mb-4" />
            <h2 className="text-4xl font-bold mb-4">Schedule Your MINI Cooper Service Today</h2>
            <p className="text-xl mb-2">
              Serving Dallas-Fort Worth from Paradise, TX | Get Directions
            </p>
            <p className="text-lg mb-8 max-w-3xl mx-auto">
              When your MINI needs specialized service, choose Paradise-Performance. Our European auto specialists understand the unique 
              requirements of MINI's distinctive engineering, providing dealer-level expertise without the premium pricing.
            </p>
            <div className="flex gap-4 justify-center">
              <Link
                to="/contact"
                className="bg-white text-[#00AA4E] px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition"
              >
                Schedule Your MINI Service Today
              </Link>
              <a
                href="tel:8173822316"
                className="bg-black text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-900 transition"
              >
                (817) 382-2316
              </a>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default MiniCooperService;
